const FEEDBACK_DIALOG_MARKER = 'data-rl-feedback-dialog';
const FEEDBACK_DIALOG_SELECTOR = `[${FEEDBACK_DIALOG_MARKER}], [role="alertdialog"]`;
const FEEDBACK_DIALOG_TITLE_ID = 'rl-feedback-dialog-title';
const CATEGORY_BUTTON_TEST_ID = 'picker-button';
const CATEGORY_LABELS = new Set(['Give a compliment', 'Report a problem', 'Make a suggestion']);
const SCREENSHOT_BUTTON_MARKER = 'data-rl-feedback-screenshot-button';
const SCREENSHOT_LIST_MARKER = 'data-rl-feedback-screenshot-list';
const SCREENSHOT_LIST_ITEM_MARKER = 'data-rl-feedback-screenshot-list-item';
const SCREENSHOT_FILE_NAME = 'screenshot.png';
const FEEDBACK_STATUS_ID = 'rl-feedback-status';
const CAPTURE_SCREENSHOT_SELECTOR = 'button[data-iaf-id="file-attachment"]';
const CAPTURE_SCREENSHOT_NAME = 'capture screenshot';
const CAPTURE_SYSTEM_SCREENSHOTS_NAME = 'capture system screenshots';
const SCREENSHOT_OPTION_MARKER = 'data-rl-feedback-screenshot-option';
const SCREENSHOT_LISTBOX_ID = 'rl-feedback-screenshot-listbox';
const ADD_MORE_DETAILS_NAME = 'add more details';
const FEEDBACK_LINK_NAMES = new Map([
  ['learn more', 'Learn more about Microsoft feedback privacy'],
  ['privacy statement', 'Microsoft feedback privacy statement'],
]);
const FOCUS_SETTLE_MS = 100;
const MAX_WAIT_MS = 1000;
const FEEDBACK_DIALOG_TITLES = new Set([
  'submit feedback to microsoft',
  'what did you like?',
  'what did you dislike?',
  'what can we do better?',
]);
const FEEDBACK_ROOT_DIALOG_TITLE = 'submit feedback to microsoft';
const FEEDBACK_DETAIL_DIALOG_TITLES = new Set([
  'what did you like?',
  'what did you dislike?',
  'what can we do better?',
]);

let activeDocument: Document | null = null;
let clickHandler: ((event: MouseEvent) => void) | null = null;
let keydownHandler: ((event: KeyboardEvent) => void) | null = null;
let screenshotObserver: MutationObserver | null = null;
let pendingObserver: MutationObserver | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let statusTimer: ReturnType<typeof setTimeout> | null = null;
let dialogFocusTimer: ReturnType<typeof setTimeout> | null = null;
let lastCategoryLabel: string | null = null;
let screenshotCount = 0;
let dialogTitleId = 0;
let preparedDialogTitles = new WeakMap<HTMLElement, string>();
let selectedScreenshotOption = CAPTURE_SCREENSHOT_NAME;

const clearPendingFocus = (): void => {
  pendingObserver?.disconnect();
  pendingObserver = null;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
};

const waitForElement = (document: Document, findElement: () => HTMLElement | null): void => {
  clearPendingFocus();

  const focusElement = (): boolean => {
    const element = findElement();
    if (!element) return false;

    clearPendingFocus();
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      element.focus();
    }, FOCUS_SETTLE_MS);
    return true;
  };

  if (focusElement()) return;

  pendingObserver = new MutationObserver(focusElement);
  pendingObserver.observe(document.body, { childList: true, subtree: true });
  pendingTimer = setTimeout(clearPendingFocus, MAX_WAIT_MS);
};

const findCategoryButton = (document: Document, label: string): HTMLButtonElement | null => {
  const dialog = document.querySelector(FEEDBACK_DIALOG_SELECTOR);
  return (
    Array.from(
      dialog?.querySelectorAll<HTMLButtonElement>(`button[data-testid="${CATEGORY_BUTTON_TEST_ID}"]`) ?? []
    ).find(
      (button) =>
        button.getAttribute('aria-label') === label &&
        (button.offsetParent !== null || button.getClientRects().length > 0)
    ) ?? null
  );
};

const getButtonName = (button: HTMLButtonElement): string =>
  (button.getAttribute('aria-label') ?? button.getAttribute('title') ?? button.textContent ?? '').trim();

const prepareFeedbackDialog = (dialog: HTMLElement): HTMLElement | null => {
  const title = dialog?.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
  if (!title) return null;

  const normalizedTitle = (title.textContent ?? '').trim().toLowerCase();
  const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
  const closeButton = buttons.find((button) => getButtonName(button).toLowerCase().startsWith('close'));
  if (normalizedTitle === FEEDBACK_ROOT_DIALOG_TITLE && !closeButton) return null;

  if (!title.id) {
    dialogTitleId += 1;
    title.id = `${FEEDBACK_DIALOG_TITLE_ID}-${dialogTitleId}`;
  }
  dialog.setAttribute(FEEDBACK_DIALOG_MARKER, 'true');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', title.id);
  dialog.tabIndex = -1;

  if (!dialog.querySelector(`#${FEEDBACK_STATUS_ID}`)) {
    const status = document.createElement('div');
    status.id = FEEDBACK_STATUS_ID;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    Object.assign(status.style, {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: '0',
    });
    dialog.appendChild(status);
  }

  const firstInteractiveElement = dialog.querySelector<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  if (!closeButton && !firstInteractiveElement) {
    title.tabIndex = -1;
  }
  return closeButton ?? firstInteractiveElement ?? title;
};

const focusNewFeedbackDialog = (document: Document): void => {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')).filter(
    (dialog) => {
      if (dialog.hasAttribute(FEEDBACK_DIALOG_MARKER)) return true;
      const title = dialog.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
      return FEEDBACK_DIALOG_TITLES.has((title?.textContent ?? '').trim().toLowerCase());
    }
  );
  const dialog = dialogs.reverse().find((candidate) => {
    const title = candidate.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
    return preparedDialogTitles.get(candidate) !== (title?.textContent ?? '').trim().toLowerCase();
  });
  if (!dialog) return;

  const focusTarget = prepareFeedbackDialog(dialog);
  if (!focusTarget) return;

  const title = dialog.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
  preparedDialogTitles.set(dialog, (title?.textContent ?? '').trim().toLowerCase());
  if (dialogFocusTimer) clearTimeout(dialogFocusTimer);
  dialogFocusTimer = setTimeout(() => {
    dialogFocusTimer = null;
    focusTarget.focus();
  }, FOCUS_SETTLE_MS);
};

const getScreenshotLinks = (document: Document): HTMLAnchorElement[] => {
  const dialog = document.querySelector(FEEDBACK_DIALOG_SELECTOR);
  return Array.from(dialog?.querySelectorAll<HTMLAnchorElement>('a') ?? []).filter((link) => {
    const accessibleName = link.getAttribute('aria-label') ?? link.getAttribute('title') ?? link.textContent ?? '';
    return accessibleName.trim().toLowerCase().includes(SCREENSHOT_FILE_NAME);
  });
};

const enforceScreenshotButtonSemantics = (document: Document): void => {
  getScreenshotLinks(document).forEach((link) => {
    if (link.hasAttribute(SCREENSHOT_BUTTON_MARKER)) return;
    link.setAttribute(SCREENSHOT_BUTTON_MARKER, 'true');
    link.setAttribute('role', 'button');
    link.addEventListener('keydown', (event) => {
      if (event.key !== ' ') return;
      event.preventDefault();
      link.click();
    });
  });
};

const enforceScreenshotListSemantics = (document: Document): void => {
  document
    .querySelectorAll<HTMLElement>(`[${SCREENSHOT_LIST_MARKER}], [${SCREENSHOT_LIST_ITEM_MARKER}]`)
    .forEach((element) => {
      element.removeAttribute('role');
      element.removeAttribute(SCREENSHOT_LIST_MARKER);
      element.removeAttribute(SCREENSHOT_LIST_ITEM_MARKER);
    });

  const screenshotLinks = getScreenshotLinks(document);
  const screenshotItems = screenshotLinks
    .map((link) => link.parentElement)
    .filter((item): item is HTMLElement => item !== null);
  const screenshotLists = new Set(
    screenshotItems.map((item) => item.parentElement).filter((list): list is HTMLElement => list !== null)
  );

  screenshotLists.forEach((list) => {
    const listItems = screenshotItems.filter((item) => item.parentElement === list);
    if (listItems.length === 0) return;

    list.setAttribute(SCREENSHOT_LIST_MARKER, 'true');
    list.setAttribute('role', 'list');
    listItems.forEach((item) => {
      item.setAttribute(SCREENSHOT_LIST_ITEM_MARKER, 'true');
      item.setAttribute('role', 'listitem');
    });
  });
};

const enforceSingleCaptureScreenshotTabStop = (document: Document): void => {
  document.querySelectorAll<HTMLButtonElement>(CAPTURE_SCREENSHOT_SELECTOR).forEach((captureButton) => {
    const dialog = captureButton.closest<HTMLElement>(FEEDBACK_DIALOG_SELECTOR);
    if (!dialog) return;

    dialog.querySelectorAll<HTMLElement>('input[type="file"], a[href], button, [tabindex]').forEach((candidate) => {
      if (candidate === captureButton) return;

      const accessibleName =
        candidate.getAttribute('aria-label') ?? candidate.getAttribute('title') ?? candidate.textContent ?? '';
      const isDuplicateCaptureControl = accessibleName.trim().toLowerCase().includes(CAPTURE_SCREENSHOT_NAME);
      if (candidate.matches('input[type="file"]') || isDuplicateCaptureControl) {
        candidate.tabIndex = -1;
      }
    });
  });
};

const enforceScreenshotDropdownSelection = (document: Document): void => {
  document.querySelectorAll<HTMLButtonElement>(CAPTURE_SCREENSHOT_SELECTOR).forEach((captureButton) => {
    const dialog = captureButton.closest<HTMLElement>(FEEDBACK_DIALOG_SELECTOR);
    if (!dialog) return;

    const screenshotOptions = Array.from(
      dialog.querySelectorAll<HTMLElement>('[role="menuitem"], [role="option"]')
    ).filter((option) => {
      const optionName = (option.getAttribute('aria-label') ?? option.textContent ?? '').trim().toLowerCase();
      return [CAPTURE_SCREENSHOT_NAME, CAPTURE_SYSTEM_SCREENSHOTS_NAME].includes(optionName);
    });
    const listbox = screenshotOptions[0]?.closest<HTMLElement>('[role="menu"], [role="listbox"]');
    if (!listbox) return;

    const sdkSelectedOption = screenshotOptions.find(
      (option) =>
        option.getAttribute('aria-selected') === 'true' ||
        option.getAttribute('aria-checked') === 'true' ||
        option.getAttribute('data-state') === 'checked'
    );
    if (sdkSelectedOption) {
      selectedScreenshotOption = (
        sdkSelectedOption.getAttribute('aria-label') ??
        sdkSelectedOption.textContent ??
        CAPTURE_SCREENSHOT_NAME
      )
        .trim()
        .toLowerCase();
    }

    listbox.id = SCREENSHOT_LISTBOX_ID;
    listbox.setAttribute('role', 'listbox');
    captureButton.setAttribute('role', 'combobox');
    captureButton.setAttribute('aria-haspopup', 'listbox');
    captureButton.setAttribute('aria-controls', SCREENSHOT_LISTBOX_ID);

    screenshotOptions.forEach((option) => {
      const optionName = (option.getAttribute('aria-label') ?? option.textContent ?? '').trim().toLowerCase();
      option.setAttribute(SCREENSHOT_OPTION_MARKER, optionName);
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(optionName === selectedScreenshotOption));
    });
  });
};

const removeAddMoreDetailsLandmark = (document: Document): void => {
  document.querySelectorAll<HTMLElement>(FEEDBACK_DIALOG_SELECTOR).forEach((dialog) => {
    dialog.querySelectorAll<HTMLElement>('[role="region"]').forEach((region) => {
      const labelledBy = region.getAttribute('aria-labelledby');
      const labelledByText = labelledBy
        ? labelledBy
            .split(/\s+/)
            .map((id) => document.getElementById(id)?.textContent ?? '')
            .join(' ')
        : '';
      const accessibleName = region.getAttribute('aria-label') ?? labelledByText;

      if (accessibleName.trim().toLowerCase().includes(ADD_MORE_DETAILS_NAME)) {
        region.removeAttribute('role');
      }
    });
  });
};

const enforceDescriptiveFeedbackLinkNames = (document: Document): void => {
  document.querySelectorAll<HTMLElement>(FEEDBACK_DIALOG_SELECTOR).forEach((dialog) => {
    dialog.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
      const currentName = (link.textContent ?? '').trim().toLowerCase();
      const descriptiveName = FEEDBACK_LINK_NAMES.get(currentName);
      if (descriptiveName) link.setAttribute('aria-label', descriptiveName);
    });
  });
};

const syncScreenshotRemoval = (document: Document): void => {
  const currentCount = getScreenshotLinks(document).length;
  if (currentCount < screenshotCount) {
    const status = document.getElementById(FEEDBACK_STATUS_ID);
    if (status) {
      status.textContent = '';
      statusTimer = setTimeout(() => {
        statusTimer = null;
        status.textContent = 'Screenshot removed';
      }, 0);
    }

    const captureButton = document.querySelector<HTMLButtonElement>(CAPTURE_SCREENSHOT_SELECTOR);
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      captureButton?.focus();
    }, FOCUS_SETTLE_MS);
  }
  screenshotCount = currentCount;
};

export const enforceFeedbackFocusRestoration = (container: HTMLElement): void => {
  stopEnforcingFeedbackFocusRestoration();
  const document = container.ownerDocument;
  activeDocument = document;
  focusNewFeedbackDialog(document);
  enforceScreenshotButtonSemantics(document);
  enforceScreenshotListSemantics(document);
  enforceSingleCaptureScreenshotTabStop(document);
  enforceScreenshotDropdownSelection(document);
  removeAddMoreDetailsLandmark(document);
  enforceDescriptiveFeedbackLinkNames(document);
  screenshotCount = getScreenshotLinks(document).length;
  screenshotObserver = new MutationObserver(() => {
    focusNewFeedbackDialog(document);
    enforceScreenshotButtonSemantics(document);
    enforceScreenshotListSemantics(document);
    enforceSingleCaptureScreenshotTabStop(document);
    enforceScreenshotDropdownSelection(document);
    removeAddMoreDetailsLandmark(document);
    enforceDescriptiveFeedbackLinkNames(document);
    syncScreenshotRemoval(document);
  });
  screenshotObserver.observe(document.body, { childList: true, subtree: true });

  clickHandler = (event: MouseEvent) => {
    const screenshotOption = (event.target as Element | null)?.closest<HTMLElement>(`[${SCREENSHOT_OPTION_MARKER}]`);
    if (screenshotOption) {
      selectedScreenshotOption = screenshotOption.getAttribute(SCREENSHOT_OPTION_MARKER) ?? CAPTURE_SCREENSHOT_NAME;
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        if (activeDocument) enforceScreenshotDropdownSelection(activeDocument);
      }, 0);
      return;
    }

    const button = (event.target as Element | null)?.closest<HTMLButtonElement>('button');
    const dialog = button?.closest<HTMLElement>(FEEDBACK_DIALOG_SELECTOR);
    if (!button || !dialog) return;

    const label = getButtonName(button);
    const dialogTitle = dialog.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
    const isDetailDialog = FEEDBACK_DETAIL_DIALOG_TITLES.has((dialogTitle?.textContent ?? '').trim().toLowerCase());
    const normalizedLabel = label.toLowerCase();
    const isCancelOrClose = normalizedLabel.startsWith('cancel') || normalizedLabel.startsWith('close');
    if (isDetailDialog && isCancelOrClose) {
      const backButton = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button')).find(
        (candidate) => getButtonName(candidate).toLowerCase() === 'back'
      );
      if (backButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        backButton.click();
      }
      return;
    }

    if (button.getAttribute('data-testid') === CATEGORY_BUTTON_TEST_ID && CATEGORY_LABELS.has(label)) {
      lastCategoryLabel = label;
      return;
    }

    if (label === 'Back' && lastCategoryLabel && activeDocument) {
      const categoryLabel = lastCategoryLabel;
      const document = activeDocument;
      clearPendingFocus();
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        waitForElement(document, () => findCategoryButton(document, categoryLabel));
      }, 0);
    }
  };

  activeDocument.addEventListener('click', clickHandler, true);
  keydownHandler = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;

    const dialog = (event.target as Element | null)?.closest<HTMLElement>(FEEDBACK_DIALOG_SELECTOR);
    if (!dialog) return;

    const closeButton = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button:not([disabled])')).find(
      (button) => getButtonName(button).toLowerCase().startsWith('close')
    );
    if (!closeButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    closeButton.click();
  };
  activeDocument.addEventListener('keydown', keydownHandler, true);
};

export const restoreFeedbackTriggerFocus = (trigger: HTMLElement | null): void => {
  if (!trigger) return;

  const document = trigger.ownerDocument;
  waitForElement(document, () => (document.querySelector(FEEDBACK_DIALOG_SELECTOR) ? null : trigger));
};

export const stopEnforcingFeedbackFocusRestoration = (): void => {
  if (activeDocument && clickHandler) {
    activeDocument.removeEventListener('click', clickHandler, true);
  }
  if (activeDocument && keydownHandler) {
    activeDocument.removeEventListener('keydown', keydownHandler, true);
  }
  screenshotObserver?.disconnect();
  screenshotObserver = null;
  if (statusTimer) clearTimeout(statusTimer);
  statusTimer = null;
  if (dialogFocusTimer) clearTimeout(dialogFocusTimer);
  dialogFocusTimer = null;
  clearPendingFocus();
  activeDocument = null;
  clickHandler = null;
  keydownHandler = null;
  lastCategoryLabel = null;
  screenshotCount = 0;
  dialogTitleId = 0;
  preparedDialogTitles = new WeakMap<HTMLElement, string>();
  selectedScreenshotOption = CAPTURE_SCREENSHOT_NAME;
};
